import { lazy, Suspense, type ComponentType } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { useAuthStore } from './store/auth';
import { Shell } from './components/layout/Shell';
import { RoleGuard } from './components/guards/RoleGuard';
import { Spinner } from './components/ui/Spinner';
import { CREDITS_ROUTE_ROLES } from './lib/transfer';

// Every feature page is its own chunk, loaded on navigation instead of all
// upfront — the single main bundle was growing past 2MB with every page's
// code (including heavyweight ones like the VOD import wizard and TMDB
// search) loaded before the admin ever visits most of them.
function lazyPage<T extends ComponentType<unknown>>(
  factory: () => Promise<Record<string, T>>,
  name: string,
) {
  return lazy(() => factory().then((m) => ({ default: m[name] })));
}

const LoginPage = lazyPage(() => import('./features/auth/LoginPage'), 'LoginPage');
const ChangePasswordPage = lazyPage(() => import('./features/auth/ChangePasswordPage'), 'ChangePasswordPage');
const DashboardPage = lazyPage(() => import('./features/dashboard/DashboardPage'), 'DashboardPage');
const SubscribersPage = lazyPage(() => import('./features/subscribers/SubscribersPage'), 'SubscribersPage');
const QuickCreatePage = lazyPage(() => import('./features/subscribers/QuickCreatePage'), 'QuickCreatePage');
const RenewBatchPage = lazyPage(() => import('./features/subscribers/RenewBatchPage'), 'RenewBatchPage');
const SubscriberFormPage = lazyPage(() => import('./features/subscribers/SubscriberFormPage'), 'SubscriberFormPage');
const SubscriberActivityPage = lazyPage(() => import('./features/subscribers/SubscriberActivityPage'), 'SubscriberActivityPage');
const StreamsPage = lazyPage(() => import('./features/streams/StreamsPage'), 'StreamsPage');
const StreamFormPage = lazyPage(() => import('./features/streams/StreamFormPage'), 'StreamFormPage');
const StreamsImportPage = lazyPage(() => import('./features/streams/StreamsImportPage'), 'StreamsImportPage');
const RenamePage = lazyPage(() => import('./features/streams/rename/RenamePage'), 'RenamePage');
const LiveM3USourcesPage = lazyPage(() => import('./features/liveSources/LiveM3USourcesPage'), 'LiveM3USourcesPage');
const LiveM3USourceFormPage = lazyPage(() => import('./features/liveSources/LiveM3USourceFormPage'), 'LiveM3USourceFormPage');
const VodPage = lazyPage(() => import('./features/vod/VodPage'), 'VodPage');
const VodWizardPage = lazyPage(() => import('./features/vod/VodWizardPage'), 'VodWizardPage');
const VodImportFolderPage = lazyPage(() => import('./features/vod/VodImportFolderPage'), 'VodImportFolderPage');
const VodM3USourcesPage = lazyPage(() => import('./features/vod/VodM3USourcesPage'), 'VodM3USourcesPage');
const SeriesPage = lazyPage(() => import('./features/series/SeriesPage'), 'SeriesPage');
const SeriesWizardPage = lazyPage(() => import('./features/series/SeriesWizardPage'), 'SeriesWizardPage');
const SeriesImportPage = lazyPage(() => import('./features/series/SeriesImportPage'), 'SeriesImportPage');
const SeasonsPage = lazyPage(() => import('./features/series/SeasonsPage'), 'SeasonsPage');
const EpisodesPage = lazyPage(() => import('./features/series/EpisodesPage'), 'EpisodesPage');
const EpisodeFormPage = lazyPage(() => import('./features/series/EpisodeFormPage'), 'EpisodeFormPage');
const ResellersPage = lazyPage(() => import('./features/resellers/ResellersPage'), 'ResellersPage');
const ResellerFormPage = lazyPage(() => import('./features/resellers/ResellerFormPage'), 'ResellerFormPage');
const CreditsRoute = lazyPage(() => import('./features/resellers/CreditsRoute'), 'CreditsRoute');
const SubResellerDetailPage = lazyPage(() => import('./features/resellers/SubResellerDetailPage'), 'SubResellerDetailPage');
const MyCreditsPage = lazyPage(() => import('./features/resellers/MyCreditsPage'), 'MyCreditsPage');
const ResellerGroupsPage = lazyPage(() => import('./features/reseller-groups/ResellerGroupsPage'), 'ResellerGroupsPage');
const ResellerGroupFormPage = lazyPage(() => import('./features/reseller-groups/ResellerGroupFormPage'), 'ResellerGroupFormPage');
const PackagesPage = lazyPage(() => import('./features/packages/PackagesPage'), 'PackagesPage');
const PackageFormPage = lazyPage(() => import('./features/packages/PackageFormPage'), 'PackageFormPage');
const BouquetEditorPage = lazyPage(() => import('./features/packages/BouquetEditorPage'), 'BouquetEditorPage');
const CategoriesPage = lazyPage(() => import('./features/categories/CategoriesPage'), 'CategoriesPage');
const CategoryFormPage = lazyPage(() => import('./features/categories/CategoryFormPage'), 'CategoryFormPage');
const ChannelOrganizerPage = lazyPage(() => import('./features/categories/ChannelOrganizerPage'), 'ChannelOrganizerPage');
const ConnectionsPage = lazyPage(() => import('./features/connections/ConnectionsPage'), 'ConnectionsPage');
const RestreamersPage = lazyPage(() => import('./features/restreamers/RestreamersPage'), 'RestreamersPage');
const RestreamerDetailPage = lazyPage(() => import('./features/restreamers/RestreamerDetailPage'), 'RestreamerDetailPage');
const PortalOverviewPage = lazyPage(() => import('./features/portal/PortalOverviewPage'), 'PortalOverviewPage');
const PortalLinksPage = lazyPage(() => import('./features/portal/PortalLinksPage'), 'PortalLinksPage');
const PortalChannelsPage = lazyPage(() => import('./features/portal/PortalChannelsPage'), 'PortalChannelsPage');
const PortalRefusalsPage = lazyPage(() => import('./features/portal/PortalRefusalsPage'), 'PortalRefusalsPage');
const PortalKeysPage = lazyPage(() => import('./features/portal/PortalKeysPage'), 'PortalKeysPage');
const PortalSettingsPage = lazyPage(() => import('./features/portal/PortalSettingsPage'), 'PortalSettingsPage');
const PortalIPsPage = lazyPage(() => import('./features/portal/PortalIPsPage'), 'PortalIPsPage');
const PortalActivityPage = lazyPage(() => import('./features/portal/PortalActivityPage'), 'PortalActivityPage');
const EpgPage = lazyPage(() => import('./features/epg/EpgPage'), 'EpgPage');
const EpgSourceFormPage = lazyPage(() => import('./features/epg/EpgSourceFormPage'), 'EpgSourceFormPage');
const ServersPage = lazyPage(() => import('./features/servers/ServersPage'), 'ServersPage');
const ServerFormPage = lazyPage(() => import('./features/servers/ServerFormPage'), 'ServerFormPage');
const ServerInstallPage = lazyPage(() => import('./features/servers/ServerInstallPage'), 'ServerInstallPage');
const ServerUninstallPage = lazyPage(() => import('./features/servers/ServerUninstallPage'), 'ServerUninstallPage');
const ServerDomainsPage = lazyPage(() => import('./features/servers/ServerDomainsPage'), 'ServerDomainsPage');
const ServerDomainFormPage = lazyPage(() => import('./features/servers/ServerDomainFormPage'), 'ServerDomainFormPage');
const ServerDomainDetailPage = lazyPage(() => import('./features/servers/ServerDomainDetailPage'), 'ServerDomainDetailPage');
const ServerGPUsPage = lazyPage(() => import('./features/servers/ServerGPUsPage'), 'ServerGPUsPage');
const ServerStatsPage = lazyPage(() => import('./features/servers/ServerStatsPage'), 'ServerStatsPage');

const TicketsPage = lazyPage(() => import('./features/tickets/TicketsPage'), 'TicketsPage');
const PanelSettingsPage = lazyPage(() => import('./features/settings/PanelSettingsPage'), 'PanelSettingsPage');
const BackupsPage = lazyPage(() => import('./features/settings/BackupsPage'), 'BackupsPage');
const XuiImportPage = lazyPage(() => import('./features/settings/XuiImportPage'), 'XuiImportPage');
const DiagnosticsPage = lazyPage(() => import('./features/settings/DiagnosticsPage'), 'DiagnosticsPage');
const TwoFactorPage = lazyPage(() => import('./features/settings/TwoFactorPage'), 'TwoFactorPage');
const AnnouncementsPage = lazyPage(() => import('./features/announcements/AnnouncementsPage'), 'AnnouncementsPage');
const AnnouncementFormPage = lazyPage(() => import('./features/announcements/AnnouncementFormPage'), 'AnnouncementFormPage');
const SystemHealthPage = lazyPage(() => import('./features/health/SystemHealthPage'), 'SystemHealthPage');
const SecurityPage = lazyPage(() => import('./features/security/SecurityPage'), 'SecurityPage');
const LogsPage = lazyPage(() => import('./features/logs/LogsPage'), 'LogsPage');
const LogRetentionPage = lazyPage(() => import('./features/logs/LogRetentionPage'), 'LogRetentionPage');
const StreamTimelinePage = lazyPage(() => import('./features/logs/StreamTimelinePage'), 'StreamTimelinePage');
const TranscodingPage = lazyPage(() => import('./features/transcoding/TranscodingPage'), 'TranscodingPage');
const TranscodingFormPage = lazyPage(() => import('./features/transcoding/TranscodingFormPage'), 'TranscodingFormPage');
const LifecyclePage = lazyPage(() => import('./features/lifecycle/LifecyclePage'), 'LifecyclePage');
const OriginSourcesPage = lazyPage(() => import('./features/originSources/OriginSourcesPage'), 'OriginSourcesPage');
const OriginSourceFormPage = lazyPage(() => import('./features/originSources/OriginSourceFormPage'), 'OriginSourceFormPage');
const ProvidersPage = lazyPage(() => import('./features/providers/ProvidersPage'), 'ProvidersPage');
const MagDevicesPage = lazyPage(() => import('./features/mag/MagDevicesPage'), 'MagDevicesPage');
const AccessCodesPage = lazyPage(() => import('./features/accessCodes/AccessCodesPage'), 'AccessCodesPage');
const AccessCodeFormPage = lazyPage(() => import('./features/accessCodes/AccessCodeFormPage'), 'AccessCodeFormPage');
const ProxyPoolPage = lazyPage(() => import('./features/proxies/ProxyPoolPage'), 'ProxyPoolPage');
const ProxyFormPage = lazyPage(() => import('./features/proxies/ProxyFormPage'), 'ProxyFormPage');
const LBGroupsPage = lazyPage(() => import('./features/loadbalancer/LBGroupsPage'), 'LBGroupsPage');
const LBGroupFormPage = lazyPage(() => import('./features/loadbalancer/LBGroupFormPage'), 'LBGroupFormPage');
const DRMProfilesPage = lazyPage(() => import('./features/drm/DRMProfilesPage'), 'DRMProfilesPage');
const OutputDevicesPage = lazyPage(() => import('./features/outputDevices/OutputDevicesPage'), 'OutputDevicesPage');
const UploadedFilesPage = lazyPage(() => import('./features/files/UploadedFilesPage'), 'UploadedFilesPage');
const WatchFoldersPage = lazyPage(() => import('./features/watchFolders/WatchFoldersPage'), 'WatchFoldersPage');
const RecordingsPage = lazyPage(() => import('./features/recordings/RecordingsPage'), 'RecordingsPage');
const RecordingFormPage = lazyPage(() => import('./features/recordings/RecordingFormPage'), 'RecordingFormPage');

function PageFallback() {
  return (
    <div className="flex items-center justify-center py-24">
      <Spinner size="lg" />
    </div>
  );
}

function Lazy({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<PageFallback />}>{children}</Suspense>;
}

// A restreamer's home is its portal, not the operator's dashboard.
function HomeRoute() {
  const role = useAuthStore((s) => s.user?.role);
  if (role === 'restreamer') return <Navigate to="/portal" replace />;
  return <Lazy><DashboardPage /></Lazy>;
}

// The panel's settings are the operator's. Everyone else goes home, whatever
// role it is: a role added later gets no settings by default.
function SettingsRouter() {
  const role = useAuthStore((s) => s.user?.role);
  if (role !== 'admin' && role !== 'client') return <Navigate to="/" replace />;
  return <PanelSettingsPage />;
}

function BackupsRouter() {
  const role = useAuthStore((s) => s.user?.role);
  if (role !== 'admin' && role !== 'client') return <Navigate to="/" replace />;
  return <BackupsPage />;
}

function XuiImportRouter() {
  const role = useAuthStore((s) => s.user?.role);
  if (role !== 'admin' && role !== 'client') return <Navigate to="/" replace />;
  return <XuiImportPage />;
}

function DiagnosticsRouter() {
  const role = useAuthStore((s) => s.user?.role);
  if (role !== 'admin' && role !== 'client') return <Navigate to="/" replace />;
  return <DiagnosticsPage />;
}

export const router = createBrowserRouter([
  {
    path: '/login',
    element: <Lazy><LoginPage /></Lazy>,
  },
  {
    path: '/change-password',
    element: <Lazy><ChangePasswordPage /></Lazy>,
  },
  {
    path: '/',
    element: <Shell />,
    children: [
      { index: true, element: <HomeRoute /> },

      // Restreamer portal
      { path: 'portal',           element: <Lazy><RoleGuard allow={['restreamer']}><PortalOverviewPage /></RoleGuard></Lazy> },
      { path: 'portal/links',     element: <Lazy><RoleGuard allow={['restreamer']}><PortalLinksPage /></RoleGuard></Lazy> },
      { path: 'portal/channels',  element: <Lazy><RoleGuard allow={['restreamer']}><PortalChannelsPage /></RoleGuard></Lazy> },
      { path: 'portal/refusals',  element: <Lazy><RoleGuard allow={['restreamer']}><PortalRefusalsPage /></RoleGuard></Lazy> },
      { path: 'portal/keys',      element: <Lazy><RoleGuard allow={['restreamer']}><PortalKeysPage /></RoleGuard></Lazy> },
      { path: 'portal/settings',  element: <Lazy><RoleGuard allow={['restreamer']}><PortalSettingsPage /></RoleGuard></Lazy> },
      { path: 'portal/ips',       element: <Lazy><RoleGuard allow={['restreamer']}><PortalIPsPage /></RoleGuard></Lazy> },
      { path: 'portal/activity',  element: <Lazy><RoleGuard allow={['restreamer']}><PortalActivityPage /></RoleGuard></Lazy> },

      // Operator routes
      { path: 'announcements',         element: <Lazy><RoleGuard allow={['admin', 'client', 'reseller', 'subreseller']}><AnnouncementsPage /></RoleGuard></Lazy> },
      { path: 'announcements/new',     element: <Lazy><RoleGuard allow={['admin']}><AnnouncementFormPage /></RoleGuard></Lazy> },
      { path: 'announcements/:id/edit', element: <Lazy><RoleGuard allow={['admin']}><AnnouncementFormPage /></RoleGuard></Lazy> },
      { path: 'health',      element: <Lazy><RoleGuard allow={['admin', 'client']}><SystemHealthPage /></RoleGuard></Lazy> },

      // Client admin + platform admin
      { path: 'streams',          element: <Lazy><RoleGuard allow={['admin', 'client']}><StreamsPage /></RoleGuard></Lazy> },
      { path: 'streams/import',  element: <Lazy><RoleGuard allow={['admin', 'client']}><StreamsImportPage /></RoleGuard></Lazy> },
      { path: 'streams/rename',  element: <Lazy><RoleGuard allow={['admin', 'client']}><RenamePage /></RoleGuard></Lazy> },
      { path: 'streams/m3u-sources',          element: <Lazy><RoleGuard allow={['admin', 'client']}><LiveM3USourcesPage /></RoleGuard></Lazy> },
      { path: 'streams/m3u-sources/new',      element: <Lazy><RoleGuard allow={['admin', 'client']}><LiveM3USourceFormPage /></RoleGuard></Lazy> },
      { path: 'streams/m3u-sources/:id/edit', element: <Lazy><RoleGuard allow={['admin', 'client']}><LiveM3USourceFormPage /></RoleGuard></Lazy> },
      { path: 'streams/new',      element: <Lazy><RoleGuard allow={['admin', 'client']}><StreamFormPage /></RoleGuard></Lazy> },
      { path: 'streams/:id/edit', element: <Lazy><RoleGuard allow={['admin', 'client']}><StreamFormPage /></RoleGuard></Lazy> },
      { path: 'streams/:id/timeline', element: <Lazy><RoleGuard allow={['admin', 'client']}><StreamTimelinePage /></RoleGuard></Lazy> },
      { path: 'lifecycle',               element: <Lazy><RoleGuard allow={['admin', 'client']}><LifecyclePage /></RoleGuard></Lazy> },
      { path: 'origin-sources',          element: <Lazy><RoleGuard allow={['admin', 'client']}><OriginSourcesPage /></RoleGuard></Lazy> },
      { path: 'origin-sources/new',      element: <Lazy><RoleGuard allow={['admin', 'client']}><OriginSourceFormPage /></RoleGuard></Lazy> },
      { path: 'origin-sources/:id/edit', element: <Lazy><RoleGuard allow={['admin', 'client']}><OriginSourceFormPage /></RoleGuard></Lazy> },
      { path: 'drm-profiles',            element: <Lazy><RoleGuard allow={['admin', 'client']}><DRMProfilesPage /></RoleGuard></Lazy> },
      { path: 'output-devices',          element: <Lazy><RoleGuard allow={['admin', 'client']}><OutputDevicesPage /></RoleGuard></Lazy> },
      { path: 'files',                   element: <Lazy><RoleGuard allow={['admin', 'client']}><UploadedFilesPage /></RoleGuard></Lazy> },
      { path: 'watch-folders',           element: <Lazy><RoleGuard allow={['admin', 'client']}><WatchFoldersPage /></RoleGuard></Lazy> },
      { path: 'providers',       element: <Lazy><RoleGuard allow={['admin', 'client']}><ProvidersPage /></RoleGuard></Lazy> },
      { path: 'vod',              element: <Lazy><RoleGuard allow={['admin', 'client']}><VodPage /></RoleGuard></Lazy> },
      { path: 'vod/add',          element: <Lazy><RoleGuard allow={['admin', 'client']}><VodWizardPage /></RoleGuard></Lazy> },
      { path: 'vod/import-folder', element: <Lazy><RoleGuard allow={['admin', 'client']}><VodImportFolderPage /></RoleGuard></Lazy> },
      { path: 'vod/m3u-sources',  element: <Lazy><RoleGuard allow={['admin', 'client']}><VodM3USourcesPage /></RoleGuard></Lazy> },
      { path: 'vod/:id/edit',     element: <Lazy><RoleGuard allow={['admin', 'client']}><VodWizardPage /></RoleGuard></Lazy> },
      { path: 'series',                                          element: <Lazy><RoleGuard allow={['admin', 'client']}><SeriesPage /></RoleGuard></Lazy> },
      { path: 'series/add',                                     element: <Lazy><RoleGuard allow={['admin', 'client']}><SeriesWizardPage /></RoleGuard></Lazy> },
      { path: 'series/import',                                  element: <Lazy><RoleGuard allow={['admin', 'client']}><SeriesImportPage /></RoleGuard></Lazy> },
      { path: 'series/:id/edit',                                element: <Lazy><RoleGuard allow={['admin', 'client']}><SeriesWizardPage /></RoleGuard></Lazy> },
      { path: 'series/:id/seasons',                             element: <Lazy><RoleGuard allow={['admin', 'client']}><SeasonsPage /></RoleGuard></Lazy> },
      { path: 'series/:id/seasons/:seasonNum/episodes',                      element: <Lazy><RoleGuard allow={['admin', 'client']}><EpisodesPage /></RoleGuard></Lazy> },
      { path: 'series/:id/seasons/:seasonNum/episodes/new',               element: <Lazy><RoleGuard allow={['admin', 'client']}><EpisodeFormPage /></RoleGuard></Lazy> },
      { path: 'series/:id/seasons/:seasonNum/episodes/:epId/edit',         element: <Lazy><RoleGuard allow={['admin', 'client']}><EpisodeFormPage /></RoleGuard></Lazy> },
      { path: 'packages',              element: <Lazy><RoleGuard allow={['admin', 'client']}><PackagesPage /></RoleGuard></Lazy> },
      { path: 'packages/new',          element: <Lazy><RoleGuard allow={['admin', 'client']}><PackageFormPage /></RoleGuard></Lazy> },
      { path: 'packages/:id/edit',     element: <Lazy><RoleGuard allow={['admin', 'client']}><PackageFormPage /></RoleGuard></Lazy> },
      { path: 'packages/bouquets/:id/edit', element: <Lazy><RoleGuard allow={['admin', 'client']}><BouquetEditorPage /></RoleGuard></Lazy> },
      { path: 'categories',              element: <Lazy><RoleGuard allow={['admin', 'client']}><CategoriesPage /></RoleGuard></Lazy> },
      { path: 'categories/new',          element: <Lazy><RoleGuard allow={['admin', 'client']}><CategoryFormPage /></RoleGuard></Lazy> },
      { path: 'categories/:id/edit',     element: <Lazy><RoleGuard allow={['admin', 'client']}><CategoryFormPage /></RoleGuard></Lazy> },
      { path: 'categories/organize',     element: <Lazy><RoleGuard allow={['admin', 'client']}><ChannelOrganizerPage /></RoleGuard></Lazy> },
      { path: 'epg',                   element: <Lazy><RoleGuard allow={['admin', 'client']}><EpgPage /></RoleGuard></Lazy> },
      { path: 'epg/sources/new',       element: <Lazy><RoleGuard allow={['admin', 'client']}><EpgSourceFormPage /></RoleGuard></Lazy> },
      { path: 'epg/sources/:id/edit',  element: <Lazy><RoleGuard allow={['admin', 'client']}><EpgSourceFormPage /></RoleGuard></Lazy> },
      { path: 'connections',  element: <Lazy><RoleGuard allow={['admin', 'client']}><ConnectionsPage /></RoleGuard></Lazy> },
      { path: 'restreamers',     element: <Lazy><RoleGuard allow={['admin', 'client']}><RestreamersPage /></RoleGuard></Lazy> },
      { path: 'restreamers/:id', element: <Lazy><RoleGuard allow={['admin', 'client']}><RestreamerDetailPage /></RoleGuard></Lazy> },
      { path: 'access-codes',         element: <Lazy><RoleGuard allow={['admin', 'client']}><AccessCodesPage /></RoleGuard></Lazy> },
      { path: 'access-codes/new',     element: <Lazy><RoleGuard allow={['admin', 'client']}><AccessCodeFormPage /></RoleGuard></Lazy> },
      { path: 'access-codes/:id/edit', element: <Lazy><RoleGuard allow={['admin', 'client']}><AccessCodeFormPage /></RoleGuard></Lazy> },
      { path: 'servers',               element: <Lazy><RoleGuard allow={['admin', 'client']}><ServersPage /></RoleGuard></Lazy> },
      { path: 'servers/new',           element: <Lazy><RoleGuard allow={['admin', 'client']}><ServerFormPage /></RoleGuard></Lazy> },
      { path: 'servers/:id/edit',      element: <Lazy><RoleGuard allow={['admin', 'client']}><ServerFormPage /></RoleGuard></Lazy> },
      { path: 'servers/:id/install',   element: <Lazy><RoleGuard allow={['admin', 'client']}><ServerInstallPage /></RoleGuard></Lazy> },
      { path: 'servers/:id/uninstall', element: <Lazy><RoleGuard allow={['admin', 'client']}><ServerUninstallPage /></RoleGuard></Lazy> },
      { path: 'servers/:id/domains',   element: <Lazy><RoleGuard allow={['admin', 'client']}><ServerDomainsPage /></RoleGuard></Lazy> },
      { path: 'servers/:id/domains/new', element: <Lazy><RoleGuard allow={['admin', 'client']}><ServerDomainFormPage /></RoleGuard></Lazy> },
      { path: 'servers/:id/domains/:domainId', element: <Lazy><RoleGuard allow={['admin', 'client']}><ServerDomainDetailPage /></RoleGuard></Lazy> },
      { path: 'servers/:id/stats',      element: <Lazy><RoleGuard allow={['admin', 'client']}><ServerStatsPage /></RoleGuard></Lazy> },
      { path: 'servers/:id/gpus',       element: <Lazy><RoleGuard allow={['admin', 'client']}><ServerGPUsPage /></RoleGuard></Lazy> },
      { path: 'security',    element: <Lazy><RoleGuard allow={['admin', 'client']}><SecurityPage /></RoleGuard></Lazy> },
      { path: 'logs',           element: <Navigate to="/logs/stream-events" replace /> },
      { path: 'logs/retention', element: <Lazy><RoleGuard allow={['admin', 'client']}><LogRetentionPage /></RoleGuard></Lazy> },
      { path: 'logs/:type',     element: <Lazy><RoleGuard allow={['admin', 'client']}><LogsPage /></RoleGuard></Lazy> },
      { path: 'transcoding',         element: <Lazy><RoleGuard allow={['admin', 'client']}><TranscodingPage /></RoleGuard></Lazy> },
      { path: 'transcoding/new',     element: <Lazy><RoleGuard allow={['admin', 'client']}><TranscodingFormPage /></RoleGuard></Lazy> },
      { path: 'transcoding/:id/edit', element: <Lazy><RoleGuard allow={['admin', 'client']}><TranscodingFormPage /></RoleGuard></Lazy> },
      { path: 'proxy-pool',           element: <Lazy><RoleGuard allow={['admin', 'client']}><ProxyPoolPage /></RoleGuard></Lazy> },
      { path: 'proxy-pool/new',       element: <Lazy><RoleGuard allow={['admin', 'client']}><ProxyFormPage /></RoleGuard></Lazy> },
      { path: 'proxy-pool/:id/edit',  element: <Lazy><RoleGuard allow={['admin', 'client']}><ProxyFormPage /></RoleGuard></Lazy> },
      { path: 'load-balancer', element: <Lazy><RoleGuard allow={['admin', 'client']}><LBGroupsPage /></RoleGuard></Lazy> },
      { path: 'load-balancer/new', element: <Lazy><RoleGuard allow={['admin', 'client']}><LBGroupFormPage /></RoleGuard></Lazy> },
      { path: 'load-balancer/:id/edit', element: <Lazy><RoleGuard allow={['admin', 'client']}><LBGroupFormPage /></RoleGuard></Lazy> },
      { path: 'mag',               element: <Lazy><RoleGuard allow={['admin', 'client']}><MagDevicesPage /></RoleGuard></Lazy> },
      { path: 'recordings',        element: <Lazy><RoleGuard allow={['admin', 'client']}><RecordingsPage /></RoleGuard></Lazy> },
      { path: 'recordings/new',    element: <Lazy><RoleGuard allow={['admin', 'client']}><RecordingFormPage /></RoleGuard></Lazy> },
      { path: 'recordings/:id/edit', element: <Lazy><RoleGuard allow={['admin', 'client']}><RecordingFormPage /></RoleGuard></Lazy> },

      // All authenticated roles
      {
        path: 'subscribers',
        element: (
          <Lazy>
            <RoleGuard allow={['admin', 'client', 'reseller', 'subreseller']}>
              <SubscribersPage />
            </RoleGuard>
          </Lazy>
        ),
      },
      {
        path: 'subscribers/quick',
        element: (
          <Lazy>
            <RoleGuard allow={['reseller', 'subreseller']}>
              <QuickCreatePage />
            </RoleGuard>
          </Lazy>
        ),
      },
      {
        path: 'subscribers/renew',
        element: (
          <Lazy>
            <RoleGuard allow={['reseller', 'subreseller']}>
              <RenewBatchPage />
            </RoleGuard>
          </Lazy>
        ),
      },
      {
        path: 'subscribers/new',
        element: (
          <Lazy>
            <RoleGuard allow={['admin', 'client', 'reseller', 'subreseller']}>
              <SubscriberFormPage />
            </RoleGuard>
          </Lazy>
        ),
      },
      {
        path: 'subscribers/:id/edit',
        element: (
          <Lazy>
            <RoleGuard allow={['admin', 'client', 'reseller', 'subreseller']}>
              <SubscriberFormPage />
            </RoleGuard>
          </Lazy>
        ),
      },
      {
        path: 'subscribers/:id/activity',
        element: (
          <Lazy>
            <RoleGuard allow={['admin', 'client', 'reseller', 'subreseller']}>
              <SubscriberActivityPage />
            </RoleGuard>
          </Lazy>
        ),
      },
      {
        path: 'resellers',
        element: (
          <Lazy>
            <RoleGuard allow={['admin', 'client', 'reseller', 'subreseller']}>
              <ResellersPage />
            </RoleGuard>
          </Lazy>
        ),
      },
      {
        path: 'resellers/new',
        element: (
          <Lazy>
            <RoleGuard allow={['admin', 'client', 'reseller']}>
              <ResellerFormPage />
            </RoleGuard>
          </Lazy>
        ),
      },
      {
        path: 'resellers/:id',
        element: (
          <Lazy>
            <RoleGuard allow={['reseller']}>
              <SubResellerDetailPage />
            </RoleGuard>
          </Lazy>
        ),
      },
      {
        path: 'resellers/:id/edit',
        element: (
          <Lazy>
            <RoleGuard allow={['admin', 'client', 'reseller']}>
              <ResellerFormPage />
            </RoleGuard>
          </Lazy>
        ),
      },
      {
        path: 'resellers/:id/credits',
        element: (
          <Lazy>
            <RoleGuard allow={[...CREDITS_ROUTE_ROLES]}>
              <CreditsRoute />
            </RoleGuard>
          </Lazy>
        ),
      },
      {
        path: 'credits',
        element: (
          <Lazy>
            <RoleGuard allow={['reseller', 'subreseller']}>
              <MyCreditsPage />
            </RoleGuard>
          </Lazy>
        ),
      },
      {
        path: 'reseller-groups',
        element: (
          <Lazy>
            <RoleGuard allow={['admin', 'client']}>
              <ResellerGroupsPage />
            </RoleGuard>
          </Lazy>
        ),
      },
      {
        path: 'reseller-groups/new',
        element: (
          <Lazy>
            <RoleGuard allow={['admin', 'client']}>
              <ResellerGroupFormPage />
            </RoleGuard>
          </Lazy>
        ),
      },
      {
        path: 'reseller-groups/:id/edit',
        element: (
          <Lazy>
            <RoleGuard allow={['admin', 'client']}>
              <ResellerGroupFormPage />
            </RoleGuard>
          </Lazy>
        ),
      },

      // Everyone
      { path: 'settings', element: <Lazy><SettingsRouter /></Lazy> },
      { path: 'settings/2fa', element: <Lazy><TwoFactorPage /></Lazy> },
      { path: 'settings/backups', element: <Lazy><BackupsRouter /></Lazy> },
      { path: 'settings/import-xui', element: <Lazy><XuiImportRouter /></Lazy> },
      { path: 'settings/diagnostics', element: <Lazy><DiagnosticsRouter /></Lazy> },
      { path: 'tickets',  element: <Lazy><TicketsPage /></Lazy> },


      { path: '*', element: <Navigate to="/" replace /> },
    ],
  },
]);
